import React, { ReactNode } from "react";
import {
	DataComponent,
	DataObserveKey,
	FocusableComponent,
	VisualComponent,
	InlineText,
	cn,
} from "@siteimprove/fancylib";
import { FormControl } from "../form/form";
import { assignKnobName } from "../../../utils/documentation-utils";
import * as scss from "./radios.scss";

/* --- Radios Context --- */
type RadiosContextProps = {
	selectedValue: string;
	onChange: (value: string) => void;
};

const RadiosContext = React.createContext<RadiosContextProps | null>(null);

/* --- Radios --- */
export type RadiosProps<T extends string> = FormControl<T> & {
	children: ReactNode;
	direction?: "horizontal" | "vertical";
} & DataObserveKey &
	VisualComponent;

export const Radios = <T extends string>(props: RadiosProps<T>): JSX.Element => {
	const { children, className, direction = "vertical", id, onChange, style, value } = props;

	return (
		<RadiosContext.Provider
			value={{
				selectedValue: value,
				onChange: onChange as (newValue: string) => void,
			}}
		>
			<div
				aria-describedby={props["aria-describedby"]}
				aria-label={props["aria-label"]}
				aria-labelledby={props["aria-labelledby"]}
				className={cn(className, scss.radios, direction === "vertical" && scss.vertical)}
				data-component="radios"
				data-observe-key={props["data-observe-key"]}
				id={id}
				role="radiogroup"
				style={style}
			>
				{children}
			</div>
		</RadiosContext.Provider>
	);
};

/* --- Radio --- */
export type RadioProps<T extends string> = {
	children: ReactNode;
	value: T;
	name?: string;
	disabled?: boolean;
} & DataObserveKey &
	DataComponent &
	FocusableComponent &
	VisualComponent;

const Radio = React.forwardRef(
	<T extends string>(props: RadioProps<T>, ref?: React.Ref<HTMLInputElement>): JSX.Element => {
		const context = React.useContext(RadiosContext);
		if (context === null)
			throw new Error("The `Radios.Radio` component must be wrapped by a `Radios` component");

		const { onChange, selectedValue } = context;
		const { value, children, name, disabled, className, ...rest } = props;
		const checked = selectedValue === value;

		const renderContent = (): React.ReactNode => {
			if (
				// string like children
				typeof children === "string" ||
				// number like children
				typeof children === "number"
			) {
				return (
					<InlineText
						emphasis={checked ? "medium" : "normal"}
						lineHeight="multi-line"
						tone={disabled ? "subtle" : undefined}
					>
						{children}
					</InlineText>
				);
			}

			// function like children
			if (typeof children === "function") {
				return children({ checked, disabled });
			}

			return children;
		};

		return (
			<label className={scss.radioLabel} data-component="radio">
				<input
					checked={checked}
					className={cn(scss.radio, className)}
					disabled={disabled}
					name={name}
					onChange={() => onChange(value)}
					ref={ref}
					type="radio"
					value={value}
					{...rest}
				/>
				{renderContent()}
			</label>
		);
	}
);

assignKnobName(Radios);
Radios.displayName = "Radios";
Radios.Radio = Radio;
